import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { notify } from 'messages';
import { flattenQuery } from 'helpers/flattenQuery';
import { extractErrorMessage } from 'helpers/extractErrorMessage';
import { getTriggers as fetchTriggers, type TriggerRow, type TriggersPage } from 'api/triggers/endpoints/getTriggers';
import { postTrigger, type SaveTrigger } from 'api/triggers/endpoints/postTrigger';
import { putTrigger } from 'api/triggers/endpoints/putTrigger';
import { deleteTrigger as deleteTriggerRequest } from 'api/triggers/endpoints/deleteTrigger';
import type { FilterConfig, ListColumnConfig } from 'api/shared/listConfig';

import { toDraft } from './toDraft';
import { acceptDegraded } from 'validation';

interface PaginationState {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

interface TriggersState {
  // Every writer stores a boundary-validated LIST row: the read populates it and
  // delete filters it. Creates/updates go through the dialog and land here via the
  // post-close refetch, so the nested single-record shape is never written in —
  // which is what makes this array uniformly `TriggerRow` rather than a union of
  // whatever each thunk happened to receive.
  triggers: TriggerRow[];
  pagination: PaginationState;
  status: string | null;
  // The LIST's load error, and only that — `TableViewLayout` renders it as a
  // persistent banner on the page. Write failures report through a toast and must
  // not land here: a failed save would otherwise paper the list behind the open
  // modal for the rest of the editing session, since nothing clears this until the
  // next fetch.
  error: string | null;
  config: ListColumnConfig[];
  filtersConfig: FilterConfig[];
  loading: {
    getTriggersPaginated: boolean;
  };
}

const initialState: TriggersState = {
  triggers: [],
  pagination: {
    totalItems: 0,
    totalPages: 1,
    currentPage: 1,
    pageSize: 50,
  },
  status: null,
  error: null,
  config: [],
  filtersConfig: [],
  loading: {
    getTriggersPaginated: false,
  },
};

export const getTriggersPaginated = createAsyncThunk<TriggersPage, Record<string, unknown>, { rejectValue: string }>(
  'triggers/getTriggersPaginated',
  async (params, { rejectWithValue }) => {
    try {
      return acceptDegraded(await fetchTriggers(flattenQuery(params)));
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, 'Failed to fetch triggers'));
    }
  }
);

/**
 * What a write can say beyond "it worked". Neither thunk keeps the returned record
 * — the list is refetched — but it must still keep whether the receipt was
 * READABLE, because the two outcomes ask the user for different things (#1035).
 *
 * A rejected write is a write that did not happen: the server refused the body, or
 * no answer arrived. A receipt that is not `ok` — degraded or unreadable — is a
 * write that DID happen and whose confirmation we could not read in full. The rows
 * are committed by then, so reporting it as a failure would invite a retry that,
 * for the create, leaves two triggers watching the same transition, both of which
 * fire.
 */
interface WriteOutcome {
  readonly outOfSync: boolean;
}

export const createTrigger = createAsyncThunk<WriteOutcome, SaveTrigger, { rejectValue: string }>(
  'triggers/createTrigger',
  async (save, { rejectWithValue }) => {
    try {
      const receipt = await postTrigger(save);
      return { outOfSync: receipt.result !== 'ok' };
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, 'Failed to create trigger'));
    }
  }
);

export const updateTrigger = createAsyncThunk<WriteOutcome, { readonly id: number; readonly save: SaveTrigger }, { rejectValue: string }>(
  'triggers/updateTrigger',
  async ({ id, save }, { rejectWithValue }) => {
    try {
      const receipt = await putTrigger(id, save);
      return { outOfSync: receipt.result !== 'ok' };
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, 'Failed to update trigger'));
    }
  }
);

export const deleteTrigger = createAsyncThunk<number, number, { rejectValue: string }>('triggers/deleteTrigger', async (id, { rejectWithValue }) => {
  try {
    await deleteTriggerRequest(id);
    return id;
  } catch (err) {
    return rejectWithValue(extractErrorMessage(err, 'Failed to delete trigger'));
  }
});

const triggersSlice = createSlice({
  name: 'triggers',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getTriggersPaginated.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.getTriggersPaginated = true;
      })
      .addCase(getTriggersPaginated.fulfilled, (state, action) => {
        state.status = 'succeeded';

        const { triggers, config, filtersConfig, pagination } = action.payload;

        // `toDraft`, not a spread: TriggerRow carries nested readonly arrays
        // (monitor_zone_ids / monitor_zone_names / parameters), which stay readonly
        // after the outer array is spread (recipe point 9).
        state.triggers = toDraft(triggers);
        state.config = toDraft(config);
        state.filtersConfig = toDraft(filtersConfig);

        const totalPages = Math.max(1, pagination.total_pages);
        const pageSize = pagination.limit;
        const currentPage = pagination.current_page;

        state.pagination.totalItems = pagination.total_records;
        state.pagination.totalPages = totalPages;
        state.pagination.pageSize = pageSize;
        state.pagination.currentPage = currentPage > totalPages ? 1 : currentPage;

        state.loading.getTriggersPaginated = false;
      })
      .addCase(getTriggersPaginated.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? action.error.message ?? 'Failed to fetch triggers';
        state.loading.getTriggersPaginated = false;
      })

      // The write cases touch no rows — the list is refetched by the post-close
      // `refresh` — so all they carry is what to tell the user.
      // UNKEYED, unlike the delete messages below. A key coalesces repeats, which
      // is spam protection for a notification the user did not ask for — but one
      // Save click produces exactly one write, so a key here buys nothing and
      // costs the case that matters: a SECOND, DIFFERENT failure inside the first
      // toast's 3s life is dropped (two messages under one key are one message,
      // and the delivery keeps the first — #1141), leaving the earlier, wrong
      // message on screen looking like the answer to the new click. If the user
      // clicks Save five times they should see five answers.
      .addCase(createTrigger.fulfilled, (_state, action) => {
        if (action.payload.outOfSync) {
          notify.warn("Trigger created, but the server's response could not be read — reopen the list to confirm what was saved.");
        } else {
          notify.success('Trigger created successfully');
        }
      })
      .addCase(createTrigger.rejected, (_state, action) => {
        notify.error(`Failed to create trigger! ${extractErrorMessage(action.payload, 'Failed to create trigger')}`);
      })

      .addCase(updateTrigger.fulfilled, (_state, action) => {
        if (action.payload.outOfSync) {
          notify.warn("Trigger updated, but the server's response could not be read — reopen the record to confirm what was saved.");
        } else {
          notify.success('Trigger updated successfully');
        }
      })
      .addCase(updateTrigger.rejected, (_state, action) => {
        notify.error(`Failed to update trigger! ${extractErrorMessage(action.payload, 'Failed to update trigger')}`);
      })

      .addCase(deleteTrigger.fulfilled, (state, action) => {
        state.triggers = state.triggers.filter((t) => t.trigger_id !== action.payload);
        // KEYED, unlike create/update: `deleteExecutor` confirms ONCE and then
        // fans out to one dispatch per selected row (`Promise.all(entities.map)`),
        // so deleting five triggers would otherwise stack five toasts for a single
        // user action. The key makes one action produce one message.
        const messageKey = 'data-deleted-trigger';
        if (!notify.isShowing(messageKey)) {
          notify.success('Trigger deleted successfully', { key: messageKey });
        }
      })
      // The ONLY feedback a failed delete produces. `deleteExecutor` confirms,
      // deletes and refreshes without inspecting the outcome, and `store.dispatch`
      // of a thunk never rejects — so without this case the row simply reappeared
      // on the next refetch with nothing said (recipe point 10).
      .addCase(deleteTrigger.rejected, (_state, action) => {
        // Keyed for the same bulk reason as the success case above.
        const messageKey = 'data-failed-to-delete-trigger';
        if (!notify.isShowing(messageKey)) {
          notify.error(`Failed to delete trigger! ${extractErrorMessage(action.payload, 'Failed to delete trigger')}`, { key: messageKey });
        }
      });
  },
});

export default triggersSlice.reducer;
